// FAQ list shown while the runtime `helpSupport` flag is off (PROD before the Help release).
import { useState, useRef, useEffect } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { useTranslation } from '../context/TranslationContext';
import styles from '../styles/FAQsLegacy.module.css';

/**
 * Rich Text rendering options
 */
const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p style={{ marginBottom: '1rem', lineHeight: 1.6 }}>
        {children}
      </p>
    ),
  },
};

export default function FAQsLegacy({ faqs = [] }) {
  if (!faqs.length) return null;

  return (
    <div className={styles.faqs}>
      {faqs.map(group => (
        <FAQGroup key={group.id} group={group} />
      ))}
    </div>
  );
}

function FAQGroup({ group }) {
  // ✅ hooks first
  const { language } = useTranslation();

  // ✅ guards after hooks
  if (!group || !group.faqs || !group.faqs.length) return null;

  const title =
    language === 'pt'
      ? group.titlePt || group.title
      : group.title;

  return (
    <section className={styles.group}>
      <h2 className={styles.groupTitle}>{title}</h2>

      <div className={styles.items}>
        {group.faqs.map(faq => (
          <FAQItem key={faq?.id} faq={faq} />
        ))}
      </div>
    </section>
  );
}

function FAQItem({ faq }) {
  // ✅ ALL hooks first — no exceptions
  const { language } = useTranslation();
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);

  const question =
    language === 'pt'
      ? faq?.titlePt || faq?.title
      : faq?.title;

  const answer =
    language === 'pt'
      ? faq?.copyPt || faq?.copy
      : faq?.copy;

  // ✅ useEffect BEFORE any return
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    el.style.maxHeight = open
      ? `${el.scrollHeight}px`
      : '0px';
  }, [open, answer]);

  // ✅ guard AFTER all hooks
  if (!faq || !question || !answer) {
    return null;
  }

  return (
    <div className={`${styles.item} ${open ? styles.open : ''}`}>
      <button
        type="button"
        className={styles.question}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <span className={styles.icon} aria-hidden>
          {open ? '−' : '+'}
        </span>
      </button>

      <div
        ref={contentRef}
        className={styles.answerWrapper}
        aria-hidden={!open}
      >
        <div className={styles.answer}>
          {documentToReactComponents(answer, richTextOptions)}
        </div>
      </div>
    </div>
  );
}